import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cable, MoreVertical, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { formatDateTimeTitle, timeAgo } from '@/_shared/lib/format';

import { SUB_HEADING } from './constants';
import { DisconnectClientDialog } from './DisconnectClientDialog';
import { SectionLoader } from './McpSectionPrimitives';
import type { McpClient } from './types';
import { clientDisplayName, extractDomain } from './utils';

export function ConnectedClientsSection(): React.ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();

  const { data, isLoading } = useQuery(orpc.mcpClient.list.queryOptions({ input: {} }));
  const clients: McpClient[] = data?.clients ?? [];

  const [pendingDisconnect, setPendingDisconnect] = useState<{ clientId: string; name: string } | null>(null);

  const revokeMutation = useMutation(
    orpc.mcpClient.revoke.mutationOptions({
      onSuccess: () => {
        setPendingDisconnect(null);
        toast.success('Client disconnected');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpClient.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke client'),
    }),
  );

  const confirmDisconnect = useCallback(
    (clientId: string) => {
      revokeMutation.mutate({ clientId });
    },
    [revokeMutation],
  );

  const clientColumns = useMemo<TableColumn<McpClient>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        sortable: false,
        cell: (client) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="size-8 shrink-0">
              <AvatarImage src={client.avatarUrl ?? undefined} alt={client.displayName} />
              <AvatarFallback>{client.displayName[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{client.displayName}</p>
              {client.email ? <p className="truncate text-xs text-muted-foreground">{client.email}</p> : null}
            </div>
          </div>
        ),
      },
      {
        id: 'client',
        header: 'Client',
        sortable: false,
        wrap: true,
        width: '18rem',
        cell: (client) => {
          const redirectUri = client.redirectUris[0];
          const domain = redirectUri ? extractDomain(redirectUri) : null;
          const scopes = client.scope.split(' ').filter(Boolean);
          return (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{clientDisplayName(client.clientName)}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {domain ? (
                  <Badge variant="info" className="text-[0.625rem]">
                    {domain}
                  </Badge>
                ) : null}
                {scopes.includes('mcp:read') ? (
                  <Badge variant="success" className="font-mono text-[0.625rem]">
                    mcp:read
                  </Badge>
                ) : null}
                {scopes.includes('mcp:write') ? (
                  <Badge variant="warning" className="font-mono text-[0.625rem]">
                    mcp:write
                  </Badge>
                ) : null}
                {scopes.includes('context:manage') ? (
                  <Badge variant="warning" className="font-mono text-[0.625rem]">
                    context:manage
                  </Badge>
                ) : null}
                {scopes.includes('mcp:audiences') ? (
                  <Badge variant="warning" className="font-mono text-[0.625rem]">
                    mcp:audiences
                  </Badge>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        id: 'activity',
        header: 'Activity',
        align: 'right',
        width: '10rem',
        sortable: false,
        cell: (client) => (
          <div className="min-w-0 text-xs text-muted-foreground">
            <p className="truncate" title={formatDateTimeTitle(client.registeredAt)}>
              Registered {timeAgo(client.registeredAt)}
            </p>
            <p className="mt-0.5 truncate" title={formatDateTimeTitle(client.lastActiveAt)}>
              Active {timeAgo(client.lastActiveAt)}
            </p>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        headerAriaLabel: 'Actions',
        align: 'right',
        width: '3.5rem',
        sortable: false,
        cell: (client) => {
          const canDelete = user?.id === client.userId || isAdmin;
          if (!canDelete) return null;
          const label = clientDisplayName(client.clientName);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${label}`} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() =>
                    setPendingDisconnect({ clientId: client.clientId, name: clientDisplayName(client.clientName) })
                  }
                >
                  <Trash2 className="size-4" />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [isAdmin, user?.id],
  );

  return (
    <>
      <div>
        <h3 className={`mb-2 ${SUB_HEADING}`}>
          Connected clients
          {clients.length > 0 && <span className="ml-1.5 text-muted-foreground/50">{clients.length}</span>}
        </h3>
        {isLoading ? (
          <SectionLoader />
        ) : clients.length === 0 ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Cable />
              </EmptyMedia>
              <EmptyTitle>No clients connected</EmptyTitle>
              <EmptyDescription>
                Follow the setup instructions above to connect Claude.ai, ChatGPT, or other MCP-compatible apps.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table
            data={clients}
            columns={clientColumns}
            getRowId={(client) => client.clientId}
            hideHeader
            fixedLayout
            maxHeight="24rem"
          />
        )}
      </div>

      <DisconnectClientDialog
        client={pendingDisconnect}
        onOpenChange={(open) => {
          if (!open) setPendingDisconnect(null);
        }}
        isDisconnecting={revokeMutation.isPending}
        onConfirm={confirmDisconnect}
      />
    </>
  );
}
